import { useMutation, useQuery } from "@tanstack/react-query";
import { StageModeAPI } from "../talent/talent_api";
import { enqueueSnackbar } from "notistack";
import { createPostApiHook } from "../../../services/api/apiHooks";
import { APIResponseType } from "../../../types/generics/APIResponse.type";


interface VoteData {
  user_name: string;
  option_id: number;
  status: "Vote" | "Unvote";
}

export type ReactionType = "Heart" | "Clap" | "Like" | "Star";

export type PerformerInvitationStatus = "pending" | "accepted" | "rejected";

export const useSendViewerInteraction = () => {
  return useMutation({
    mutationFn: (payload: {
      sessionId: string;
      type: "emoji" | "qna" | "tip" | "pollVote";
      payload: Record<string, any>;
    }) => StageModeAPI.post("/interactions", payload),
    onError: (err) => console.error("Viewer interaction failed:", err),
  });
};

export const useVoteInPoll = (bookingId: string) => {
  return useMutation({
    mutationFn: ({
      pollId,
      voteData,
    }: {
      pollId: string;
      voteData: VoteData;
    }) =>
      StageModeAPI.post(
        `stagecards/booking/${bookingId}/poll/${pollId}/vote`,
        voteData
      ),
    onSuccess: (data, variables) => {
      if (variables.voteData.status === "Vote") {
        enqueueSnackbar("You have voted successfully", {
          variant: "success",
        });
      }
    },
    onError: (err) => console.error("Poll voting failed:", err),
  });
};

export const useSendViewerTip = (bookingId: string) => {
  return useMutation({
    mutationFn: ({ amount }: { amount: number }) =>
      StageModeAPI.post(`/stagecards/booking/${bookingId}/tip`, { amount }),
    onSuccess: (_, variables) => {
      enqueueSnackbar(`You have tipped ${variables.amount} chips`, {
        variant: "success",
      });
    },
    onError: (err: any) => {
      enqueueSnackbar(err.response.data.message || "Tip submission failed", {
        variant: "error",
      });
    },
  });
};

export const useSendMessage = (bookingId: string) => {
  return useMutation({
    mutationFn: ({
      message,
      username,
    }: {
      message: string;
      username: string;
    }) =>
      StageModeAPI.post(`/stagecards/booking/${bookingId}/message`, {
        message,
        username,
      }),
    onSuccess: () => {
      enqueueSnackbar("Message sent successfully", {
        variant: "success",
      });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to send message",
        {
          variant: "error",
        }
      );
    },
  });
};

export const useGetMessages = (id: string) => {
  return useQuery({
    queryKey: ["get-messages", id],
    queryFn: async () => {
      const res = await StageModeAPI.get(`/stagecards/booking/${id}/message`, {
        params: {
          limit: 1000,
        },
      });
      return res.data;
    },
    enabled: !!id,
    staleTime: 10000,
    retry: 1,
  });
};

export const useSendReaction = (bookingId: string) => {
  return useMutation({
    mutationFn: ({ type, status }: { type: ReactionType; status: 0 | 1 }) =>
      StageModeAPI.post(`/stagecards/booking/${bookingId}/reaction`, {
        type,
        status,
      }),
    onError: (error: any) => {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to send message",
        {
          variant: "error",
        }
      );
    },
  });
};

export const useGetReaction = (id: string) => {
  return useQuery({
    queryKey: ["get-reactions", id],
    queryFn: async () => {
      const res = await StageModeAPI.get(`/stagecards/booking/${id}/reaction`);
      return res.data;
    },
    enabled: !!id,
    staleTime: 10000,
    retry: 1,
  });
};

const joinGuest = ({ id }: { id: string }) =>
  `/stagecards/booking/${id}/join`;

export const useJoinGuest = createPostApiHook<{}, {id:string,},APIResponseType<any>>(
  joinGuest,
  {
    isFormDataMethod: false,
  }
);

const sendYoutubeRequestEndpoint = ({
  id,
  userId,
}: {
  id: string | number;
  userId: string | number;
}) => `stagecards/booking/${id}/user/${userId}/youtube-url`;

export const useSendYoutubeRequest = createPostApiHook<
  {
    youtube_url?: string;
    message?: string;
    song_name_with_artist?: string;
  },
  { id: string | number; userId: string | number },
  APIResponseType<any>
>(sendYoutubeRequestEndpoint, {
  isFormDataMethod: false,
});
